import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, ShieldCheckIcon, UsersIcon, BrainIcon, WrenchIcon, RefreshCwIcon, HeadphonesIcon, ClockIcon, FileTextIcon, SettingsIcon, TrendingUpIcon, BuildingIcon, ZapIcon, LayersIcon, LockIcon } from 'lucide-react';
import { CTABanner } from '../../components/CTABanner';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5
    }
  }
};
const stagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
function SectionDivider() {
  return <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-violet-electric/20 to-transparent" data-id="element-1598" data-mp="16jp4lm1obkb6" />;
}
export function EnterprisePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen w-full bg-navy" data-id="element-1599" data-mp="23zckgg82kfo2">
      {/* ── SECTION 1: HERO ── */}
      <section className="relative w-full min-h-screen flex items-center justify-center pt-32 pb-24 px-6 overflow-hidden bg-navy" data-id="element-1600" data-mp="19hiw72749znj">
        <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(108,92,231,0.18) 0%, rgba(56,103,214,0.08) 50%, transparent 80%)'
      }} data-id="element-1601" data-mp="15gqmk7uc1sqq" />
        
        <div className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none" style={{
        background: 'linear-gradient(to top, #0D0B2E, transparent)'
      }} data-id="element-1602" data-mp="1ef2lqy99lyus" />
        

        <div className="relative z-10 max-w-3xl mx-auto text-center" data-id="element-1603" data-mp="1o5dqgn0rwc2q">
          <motion.span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-blue-400/30 bg-blue-400/10 text-blue-400 text-xs font-semibold tracking-[0.15em] uppercase mb-6" initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: 0.1
        }} data-id="element-1604" data-mp="225yatnmc6fyc">
            
            Thruline Enterprise
          </motion.span>

          <motion.h1 className="text-5xl sm:text-6xl md:text-7xl font-bold text-white leading-[1.05] tracking-tight mb-6" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.55,
          delay: 0.2
        }} data-id="element-1605" data-mp="22x6h2islyyro">
            
            Your custom ERP.
            <br data-id="element-1606" data-mp="2luzgvc7xqsku" />
            <span className="text-transparent bg-clip-text" style={{
            backgroundImage: 'linear-gradient(90deg, #60a5fa, #818cf8)'
          }} data-id="element-1607" data-mp="2ltenvr19rhp3">
              
              Built and run by experts.
            </span>
          </motion.h1>

          <motion.p className="text-lg sm:text-xl text-white/55 max-w-2xl mx-auto mb-10 leading-relaxed" initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} data-id="element-1608" data-mp="30j3zyhawh645">
            
            Thruline Enterprise is a fully custom ERP, configured to your
            actual operations and maintained by Thruline's team. We handle the
            system. You handle the business.
          </motion.p>

          <motion.div className="flex flex-col sm:flex-row items-center justify-center gap-4" initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.4
        }} data-id="element-1609" data-mp="ouyz8h308ol4">
            
            <a href="https://calendly.com/alex-thruline/learn-about-thruline" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-navy font-semibold text-sm hover:bg-white/90 transition-all hover:shadow-lg hover:shadow-white/10" data-id="element-1610" data-mp="1cl32gnca6mbl">
              
              Get a Demo
              <ArrowRightIcon className="w-4 h-4" data-id="element-1611" data-mp="20b5s6wsq3g71" />
            </a>
            <a href="https://calendly.com/alex-thruline/learn-about-thruline" className="inline-flex items-center px-8 py-3.5 rounded-full border border-white/25 text-white font-medium text-sm hover:border-white/50 transition-all" data-id="element-1612" data-mp="1vk77ll0pbvxz">
              
              Talk to our team
            </a>
          </motion.div>
        </div>
      </section>

      {/* ── SECTION 2: THE PROMISE ── */}
      <section className="relative w-full bg-navy-light py-24 px-6" data-id="element-1613" data-mp="166cm0c0haemf">
        <SectionDivider data-id="element-1614" data-mp="1b3mgi62q5gh4" />
        <div className="max-w-3xl mx-auto" data-id="element-1615" data-mp="1b4xl283zus9a">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} data-id="element-1616" data-mp="1ytq9zkm77zue">
            
            <motion.span variants={fadeUp} className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1617" data-mp="1p2vmkcx7uu7d">
              
              The Promise
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-12" data-id="element-1618" data-mp="2hrt60jd5yx1p">
              
              A system built around your operation.
              <br data-id="element-1619" data-mp="2mk2nrc44kqsw" />
              Not the other way around.
            </motion.h2>

            <div className="space-y-8" data-id="element-1620" data-mp="2vuyu6qwtr5ia">
              <motion.div variants={fadeUp} className="flex gap-5" data-id="element-1621" data-mp="2rvzubkpy9yxp">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mt-0.5" data-id="element-1622" data-mp="2rtnb748ghij2">
                  <BrainIcon className="w-4 h-4 text-blue-400" data-id="element-1623" data-mp="30otbjnoyj8e7" />
                </div>
                <div data-id="element-1624" data-mp="2cxfj7hqt3ct8">
                  <p className="text-white/70 text-base leading-relaxed" data-id="element-1625" data-mp="2cyz1tezxpzy6">
                    <span className="text-white font-medium" data-id="element-1626" data-mp="28xds22ip61s7">
                      Your business has its own way of working.
                    </span>{' '}
                    Your receiving process, your production scheduling, your
                    quality checks — they exist because they work for your
                    operation. An ERP should reflect that reality, not replace
                    it with someone else's template.
                  </p>
                </div>
              </motion.div>

              <motion.div variants={fadeUp} className="flex gap-5" data-id="element-1627" data-mp="56pkvs7vtpjm">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-violet-500/10 border border-violet-500/20 flex items-center justify-center mt-0.5" data-id="element-1628" data-mp="992oa9z5pbd8">
                  <UsersIcon className="w-4 h-4 text-violet-400" data-id="element-1629" data-mp="e98j1z0q3dzj" />
                </div>
                <div data-id="element-1630" data-mp="j70a6qtjmw2o">
                  <p className="text-white/70 text-base leading-relaxed" data-id="element-1631" data-mp="j5fq5v4uuvj2">
                    <span className="text-white font-medium" data-id="element-1632" data-mp="3hqqra7keqn0h">
                      But building a custom system takes a team most
                      manufacturers don't have.
                    </span>{' '}
                    Enterprise architects, implementation consultants, ongoing
                    developers — the cost and complexity of standing up that
                    capability internally puts truly custom ERP out of reach.
                  </p>
                </div>
              </motion.div>

              <motion.div variants={fadeUp} className="flex gap-5" data-id="element-1633" data-mp="33vwsevp058qy">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mt-0.5" data-id="element-1634" data-mp="2wd706gybnuev">
                  <ShieldCheckIcon className="w-4 h-4 text-emerald-400" data-id="element-1635" data-mp="2mg28y2pmd8r7" />
                </div>
                <div data-id="element-1636" data-mp="2hgzgdhqtsv04">
                  <p className="text-white/70 text-base leading-relaxed" data-id="element-1637" data-mp="2hhhao8txgz5e">
                    <span className="text-white font-medium" data-id="element-1638" data-mp="2dfdvpa81vcfd">
                      Thruline Enterprise is that team.
                    </span>{' '}
                    We discover how your operation actually works, build a system
                    that matches, and keep it running and evolving as your
                    business grows. You focus on your business. We focus on your
                    system.
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── SECTION 3: HOW IT WORKS ── */}
      <section className="relative w-full bg-navy py-24 px-6" data-id="element-1639" data-mp="1ps5sqyy3gfgi">
        <SectionDivider data-id="element-1640" data-mp="3dn0vrq8lpozm" />
        <div className="max-w-4xl mx-auto" data-id="element-1641" data-mp="3dkerq0kx5rm5">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} data-id="element-1642" data-mp="3ilubzqf9vu3h">
            
            <motion.span variants={fadeUp} className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1643" data-mp="38mlkd47vqrka">
              
              How It Works
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-id="element-1644" data-mp="2zmyjqkmz5psa">
              
              We discover it. We build it. We run it.
            </motion.h2>
          </motion.div>

          <motion.div className="space-y-5" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} variants={stagger} data-id="element-1645" data-mp="2pxeumx8o4zoz">
            
            {/* Phase 1 */}
            <motion.div variants={fadeUp} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden" data-id="element-1646" data-mp="2g0tq0d0k9qlo">
              
              <div className="flex items-start gap-6 p-8" data-id="element-1647" data-mp="1sbvhjpqovs40">
                <div className="flex-shrink-0" data-id="element-1648" data-mp="1sdf3nwz10xdp">
                  <div className="w-12 h-12 rounded-xl bg-violet-electric/15 border border-violet-electric/20 flex items-center justify-center" data-id="element-1649" data-mp="26bdbskmr7xkr">
                    <BrainIcon className="w-6 h-6 text-violet-electric" data-id="element-1650" data-mp="21e4v6oh981lm" />
                  </div>
                </div>
                <div className="flex-1" data-id="element-1651" data-mp="2ba7vdosjpq5x">
                  <div className="flex items-center gap-3 mb-3" data-id="element-1652" data-mp="2b956tnip3zt6">
                    <span className="text-violet-electric text-xs font-bold tracking-widest uppercase" data-id="element-1653" data-mp="2p1czp46uz6ml">
                      Phase 1
                    </span>
                    <span className="text-white/20 text-xs" data-id="element-1654" data-mp="2l0t07tytbqxa">&mdash;</span>
                    <span className="text-white font-semibold" data-id="element-1655" data-mp="2z0kb3lu18r72">Discovery</span>
                    <span className="ml-auto px-2.5 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] font-bold tracking-wide uppercase" data-id="element-1656" data-mp="2u1h4hwyemcwz">
                      Free
                    </span>
                  </div>
                  <p className="text-white/55 text-sm leading-relaxed mb-4" data-id="element-1657" data-mp="1c6xucf1akq51">
                    Thruline's AI reads your existing documents — SOPs,
                    spreadsheets, emails, PDFs — and maps your actual
                    operational processes. Our team reviews the results and
                    designs the system architecture: what your ERP needs to do,
                    how the modules connect, and why.
                  </p>
                  <p className="text-white/40 text-sm" data-id="element-1658" data-mp="1qwlx7kp4fwuv">
                    <span className="text-white/60 font-medium" data-id="element-1659" data-mp="1lyke4zrp3xje">
                      What you get:
                    </span>{' '}
                    A complete picture of your operation translated into system
                    architecture — without months of consultant interviews.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Phase 2 */}
            <motion.div variants={fadeUp} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden" data-id="element-1660" data-mp="5ekxifogqbph">
              
              <div className="flex items-start gap-6 p-8" data-id="element-1661" data-mp="acmn7jge999x">
                <div className="flex-shrink-0" data-id="element-1662" data-mp="f44etgwj1xi8">
                  <div className="w-12 h-12 rounded-xl bg-violet-electric/15 border border-violet-electric/20 flex items-center justify-center" data-id="element-1663" data-mp="f5xnf1qparsv">
                    <WrenchIcon className="w-6 h-6 text-violet-electric" data-id="element-1664" data-mp="o52k02ipcb2e" />
                  </div>
                </div>
                <div className="flex-1" data-id="element-1665" data-mp="t3mbeig2ef25">
                  <div className="flex items-center gap-3 mb-3" data-id="element-1666" data-mp="t4xc29e7r6p1">
                    <span className="text-violet-electric text-xs font-bold tracking-widest uppercase" data-id="element-1667" data-mp="y3iemfgxu2gn">
                      Phase 2
                    </span>
                    <span className="text-white/20 text-xs" data-id="element-1668" data-mp="2uxv6uarjw9cm">&mdash;</span>
                    <span className="text-white font-semibold" data-id="element-1669" data-mp="2zuvf8sbvho6w">
                      Implementation
                    </span>
                    <span className="ml-auto px-2.5 py-0.5 rounded-full bg-violet-electric/10 border border-violet-electric/20 text-violet-electric text-[10px] font-bold tracking-wide uppercase" data-id="element-1670" data-mp="2muuf3bz0huuh">
                      Fixed Quote
                    </span>
                  </div>
                  <p className="text-white/55 text-sm leading-relaxed mb-4" data-id="element-1671" data-mp="2wv3gd47v26gq">
                    Thruline builds your complete system. We handle architecture
                    decisions, workflow design, integrations, testing, and
                    deployment. You participate in review checkpoints to
                    validate that the system matches your operation — but the
                    build is on us.
                  </p>
                  <p className="text-white/40 text-sm" data-id="element-1672" data-mp="3tirl8kiww2ic">
                    <span className="text-white/60 font-medium" data-id="element-1673" data-mp="3th6r2w0iell0">
                      What you get:
                    </span>{' '}
                    A production-ready ERP configured to your actual processes,
                    delivered at a fixed price scoped after Discovery.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Phase 3 */}
            <motion.div variants={fadeUp} className="rounded-2xl border border-blue-400/15 bg-blue-400/[0.03] overflow-hidden" data-id="element-1674" data-mp="3207s9lrdlu8">
              
              <div className="flex items-start gap-6 p-8" data-id="element-1675" data-mp="3peyvwsvgc48n">
                <div className="flex-shrink-0" data-id="element-1676" data-mp="3pdnp0k6pmgdb">
                  <div className="w-12 h-12 rounded-xl bg-blue-400/20 border border-blue-400/30 flex items-center justify-center" data-id="element-1677" data-mp="3u78qim7ihsh9">
                    <HeadphonesIcon className="w-6 h-6 text-blue-400" data-id="element-1678" data-mp="3ggn5oryhz8gq" />
                  </div>
                </div>
                <div className="flex-1" data-id="element-1679" data-mp="3ke1bsaqvt04i">
                  <div className="flex items-center gap-3 mb-3" data-id="element-1680" data-mp="36gcpcyh61kxc">
                    <span className="text-blue-400 text-xs font-bold tracking-widest uppercase" data-id="element-1681" data-mp="3bfpooip84kgt">
                      Phase 3
                    </span>
                    <span className="text-white/20 text-xs" data-id="element-1682" data-mp="hmg0990morrj">&mdash;</span>
                    <span className="text-white font-semibold" data-id="element-1683" data-mp="dlw1y24bji3t">Operate</span>
                    <span className="ml-auto px-2.5 py-0.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-[10px] font-bold tracking-wide uppercase" data-id="element-1684" data-mp="8vflsa945idr">
                      Managed Service
                    </span>
                  </div>
                  <p className="text-white/55 text-sm leading-relaxed mb-4" data-id="element-1685" data-mp="3v3xdx5lfrb14">
                    You use your ERP to run your business. Thruline manages
                    everything behind the scenes: updates, system health,
                    change requests, and ongoing optimization. When your
                    business changes, you tell us what's different — we adjust
                    the system to match.
                  </p>
                  <p className="text-white/40 text-sm" data-id="element-1686" data-mp="1fbrf9y0zqc30">
                    <span className="text-white/60 font-medium" data-id="element-1687" data-mp="1fc9i5di5rojw">
                      What you get:
                    </span>{' '}
                    A system that stays aligned with your operation without
                    requiring internal technical staff to maintain it.
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── SECTION 4: WHO IT'S FOR ── */}
      <section className="relative w-full bg-navy-light py-24 px-6" data-id="element-1688" data-mp="13wy6nwfd9e3r">
        <SectionDivider data-id="element-1689" data-mp="18v8288506gpw" />
        <div className="max-w-3xl mx-auto" data-id="element-1690" data-mp="18ug52svrcaxi">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} data-id="element-1691" data-mp="18vhphnyny9rw">
            
            <motion.span variants={fadeUp} className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1692" data-mp="1hwz3n0sig1dy">
              
              Who It's For
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4" data-id="element-1693" data-mp="2t9rv0ahe2nth">
              
              For companies that want a custom ERP without the custom ERP
              headache.
            </motion.h2>
            <motion.p variants={fadeUp} className="text-white/50 text-base mb-10" data-id="element-1694" data-mp="1370zwovibgvq">
              
              Thruline Enterprise is for you if:
            </motion.p>

            <motion.div variants={stagger} className="space-y-4" data-id="element-1695" data-mp="y8ppabxe12cg">
              {['You want a fully custom ERP without any involvement in the technical build', 'You prefer to hand the system to an expert team and focus entirely on running your business', 'You have the budget for a managed service and value speed over hands-on control', 'You need complex integrations, compliance requirements, or multi-site deployments that benefit from dedicated expert management', "You don't want to invest time learning system configuration — you want it done"].map((item, i) => <motion.div key={i} variants={fadeUp} className="flex items-start gap-4" data-id="element-1696" data-mp="3iyxbagmakmru" data-mpi={i}>
                
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-400/15 border border-blue-400/25 flex items-center justify-center mt-0.5" data-id="element-1697" data-mp="1yf1wsw6nr9r8">
                    <CheckIcon className="w-3.5 h-3.5 text-blue-400" data-id="element-1698" data-mp="238o2u5ibgfe2" />
                  </div>
                  <p className="text-white/70 text-base leading-relaxed" data-id="element-1699" data-mp="1pdllmm80r3ly">
                    {item}
                  </p>
                </motion.div>)}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── SECTION 5: WHAT'S INCLUDED ── */}
      <section className="relative w-full bg-navy py-24 px-6" data-id="element-1700" data-mp="2m55pi8ib5p3g">
        <SectionDivider data-id="element-1701" data-mp="286pishkg3ylb" />
        <div className="max-w-5xl mx-auto" data-id="element-1702" data-mp="2854o8ry4sfng">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} data-id="element-1703" data-mp="2d50t0tenkb42">
            
            <motion.span variants={fadeUp} className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1704" data-mp="11puahly7swm9">
              
              What's Included
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-id="element-1705" data-mp="1oec3ni16zv81">
              
              Everything built, managed, and maintained.
            </motion.h2>
          </motion.div>

          <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} variants={stagger} data-id="element-1706" data-mp="1eh7npzxxwvzo">
            
            {[{
            icon: LayersIcon,
            title: 'Full Module Suite',
            desc: 'Production, inventory, purchasing, shipping, quality, field operations — every module your operation needs, configured and integrated by Thruline.'
          }, {
            icon: WrenchIcon,
            title: 'Thruline-Built Configuration',
            desc: 'Every workflow, rule, and automation is designed and built by Thruline\'s team based on your actual operational processes. No templates. No compromises.'
          }, {
            icon: HeadphonesIcon,
            title: 'Managed Operations',
            desc: 'System updates, performance monitoring, change requests, and ongoing optimization — all handled by Thruline. Your team uses the system; we keep it running.'
          }, {
            icon: RefreshCwIcon,
            title: 'Continuous Alignment',
            desc: 'When your business changes, the system changes with it. New product lines, new processes, new requirements — tell Thruline, and we adjust.'
          }, {
            icon: SettingsIcon,
            title: 'Integrations & Compliance',
            desc: 'Thruline handles integrations with your existing tools, compliance requirements, and multi-site coordination. Complex technical work stays off your plate.'
          }, {
            icon: FileTextIcon,
            title: 'Full Documentation',
            desc: 'Your system is fully documented and traceable. Every configuration decision links back to the operational process that informed it. Maintained by Thruline.'
          }, {
            icon: ShieldCheckIcon,
            title: 'Expert Quality Control',
            desc: 'Every change goes through Thruline\'s review process. Architecture decisions, workflow modifications, and system updates are validated by experts before deployment.'
          }, {
            icon: TrendingUpIcon,
            title: 'Expansion on Demand',
            desc: 'Ready for new modules or major system expansion? Same process — Discovery, fixed quote, Thruline builds it. Grows with your business without growing your team.'
          }, {
            icon: ClockIcon,
            title: 'Fast Delivery',
            desc: 'Thruline\'s AI-powered discovery and implementation process delivers production-ready systems in weeks, not the months or years typical of traditional ERP.'
          }].map((item, i) => {
            const IconComp = item.icon;
            return <motion.div key={i} variants={fadeUp} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-7" data-id="element-1707" data-mp="2fc34crcq5sa" data-mpi={i}>
                  
                  <div className="w-9 h-9 rounded-lg bg-blue-400/12 flex items-center justify-center mb-4" data-id="element-1708" data-mp="3uhl2tfp54qxo">
                    <IconComp className="w-4.5 h-4.5 text-blue-400" data-id="element-1709" data-mp="3plw1b6tk8zyq" />
                  </div>
                  <h3 className="text-sm font-semibold text-white mb-2" data-id="element-1710" data-mp="3kmt5mczh063f">
                    {item.title}
                  </h3>
                  <p className="text-white/45 text-sm leading-relaxed" data-id="element-1711" data-mp="h48820y432oo">
                    {item.desc}
                  </p>
                </motion.div>;
          })}
          </motion.div>
        </div>
      </section>

      {/* ── SECTION 6: WHY ENTERPRISE ── */}
      <section className="relative w-full bg-navy-light py-24 px-6" data-id="element-1712" data-mp="7dwtlkgsxb3j">
        <SectionDivider data-id="element-1713" data-mp="14um19usfn7ku" />
        <div className="max-w-4xl mx-auto" data-id="element-1714" data-mp="14uvhiqnoe0dc">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} data-id="element-1715" data-mp="1033yr2argyxr">
            
            <motion.span variants={fadeUp} className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1716" data-mp="2yc1i6rho0mxn">
              
              Common Questions
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-id="element-1717" data-mp="2pjg0leprsmae">
              
              Why Thruline Enterprise
            </motion.h2>
          </motion.div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-5" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} variants={stagger} data-id="element-1718" data-mp="2fm1wmel19pyz">
            
            {[{
            q: 'How is this different from hiring an ERP consultant?',
            a: "Traditional consultants interview your team for months, configure a system based on their interpretation, and hand you the keys. Thruline's AI reads your actual documents to understand your operation — no interpretation gap. Then we stay. We don't hand you the keys and leave. We operate and maintain the system as a managed service."
          }, {
            q: 'What if our business changes after go-live?',
            a: "That's expected. When your operation changes — new product line, new process, new supplier — you tell Thruline what's different. We scope the change, quote it if it's significant, and implement it. Standard adjustments are included in your managed service. Your system stays aligned with your business."
          }, {
            q: 'How long does implementation take?',
            a: 'Thruline\'s AI-powered discovery and build process is dramatically faster than traditional ERP implementation. Discovery takes days, not months. The full build depends on scope, but most initial implementations are measured in weeks. You\'ll get a specific timeline after Discovery.'
          }, {
            q: 'What if we want more control later?',
            a: 'Thruline also offers Core — a self-evolving platform where your team takes on day-to-day configuration with Thruline advisory support. Enterprise customers can transition to Core if they develop the internal capability and desire for more hands-on control. The two products complement each other.'
          }].map((item, i) => <motion.div key={i} variants={fadeUp} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8" data-id="element-1719" data-mp="3ju9s380jnxe8" data-mpi={i}>
              
                <h3 className="text-base font-semibold text-white mb-3 leading-snug" data-id="element-1720" data-mp="2n3hfwlgqephq">
                  "{item.q}"
                </h3>
                <p className="text-white/50 text-sm leading-relaxed" data-id="element-1721" data-mp="2w5qkkupbjon7">
                  {item.a}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ── SECTION 7: COMPARISON TABLE ── */}
      <section className="relative w-full bg-navy py-24 px-6" data-id="element-1722" data-mp="23h1l103wvrdk">
        <SectionDivider data-id="element-1723" data-mp="28fctp7igzvu7" />
        <div className="max-w-4xl mx-auto" data-id="element-1724" data-mp="28el88pil7ehn">
          <motion.div className="text-center mb-14" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} data-id="element-1725" data-mp="20wn12rnvofhl">
            
            <motion.span variants={fadeUp} className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1726" data-mp="1qyfoel0mgjle">
              
              Comparison
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-id="element-1727" data-mp="1hxrb5sj1yn9k">
              
              Enterprise vs. Traditional ERP
            </motion.h2>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-40px'
        }} transition={{
          duration: 0.5
        }} className="rounded-2xl border border-white/[0.06] overflow-hidden" data-id="element-1728" data-mp="187g6u4vgic25">
            
            <table className="w-full text-sm" data-id="element-1729" data-mp="uajefyzowmj5">
              <thead data-id="element-1730" data-mp="kkkvell5n6yp">
                <tr className="border-b border-white/[0.06]" data-id="element-1731" data-mp="kk1aqx44e4gz">
                  <th className="text-left px-6 py-4 text-white/30 font-medium text-xs uppercase tracking-wider w-1/3" data-id="element-1732" data-mp="omxq8eyd5kgp"></th>
                  <th className="text-left px-6 py-4 text-white/40 font-medium text-xs uppercase tracking-wider w-1/3" data-id="element-1733" data-mp="anolcbflycs1">
                    Traditional ERP
                  </th>
                  <th className="text-left px-6 py-4 text-blue-400 font-semibold text-xs uppercase tracking-wider w-1/3 bg-blue-400/[0.04]" data-id="element-1734" data-mp="13aslo33ra2xh">
                    Thruline Enterprise
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1735" data-mp="tl8zookqxsye">
                {[['Discovery method', 'Months of consultant interviews', 'AI reads your documents in days'], ['How it\'s configured', 'Vendor templates with workarounds', 'Built around your actual processes'], ['Implementation time', '6–18 months typical', 'Weeks, not months'], ['Pricing model', 'Unpredictable change orders', 'Fixed quote after Discovery'], ['After go-live', 'Consultant leaves, knowledge goes with them', 'Thruline stays as managed service'], ['When you need changes', 'Re-engage consultants, new SOW', 'Tell Thruline, we handle it'], ['Documentation', 'Delivered once, immediately outdated', 'Self-documenting, always current'], ['Internal team required', 'IT staff or vendor dependency', 'None — Thruline is your team'], ['System evolution', 'Expensive upgrade cycles', 'Continuous alignment with your operation']].map(([label, traditional, enterprise], i) => <tr key={i} className={`border-b border-white/[0.04] ${i % 2 === 0 ? 'bg-white/[0.01]' : ''}`} data-id="element-1736" data-mp="2nuxpd8wm1j2q" data-mpi={i}>
                  
                    <td className="px-6 py-4 text-white/50 font-medium" data-id="element-1737" data-mp="31qrq34n8d24r">
                      {label}
                    </td>
                    <td className="px-6 py-4 text-white/35" data-id="element-1738" data-mp="h2dlnaah9mc5">{traditional}</td>
                    <td className="px-6 py-4 text-white/70 bg-blue-400/[0.03]" data-id="element-1739" data-mp="c3u0vd7megld">
                      {enterprise}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>

      {/* ── SECTION 8: TWO WAYS ── */}
      <section className="relative w-full bg-navy-light py-24 px-6" data-id="element-1740" data-mp="156mqnuju5blt">
        <SectionDivider data-id="element-1741" data-mp="155bm3sikfztn" />
        <div className="max-w-4xl mx-auto" data-id="element-1742" data-mp="154s6iige1nis">
          <motion.div className="text-center mb-14" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} data-id="element-1743" data-mp="19yd3c768wbge">
            
            <motion.span variants={fadeUp} className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1744" data-mp="1jug6ngcu2g4x">
              
              Two Ways to Work With Thruline
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4" data-id="element-1745" data-mp="1svntn29wfujp">
              
              Want it done, or want to own it?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-white/45 text-base max-w-xl mx-auto" data-id="element-1746" data-mp="3uo2fgcfkdj5e">
              
              Both start with the same AI-powered Discovery. The difference is
              who holds the wrench after go-live.
            </motion.p>
          </motion.div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} variants={stagger} data-id="element-1747" data-mp="3df1dfuiir1ei">
            
            {/* Enterprise Card */}
            <motion.div variants={fadeUp} className="rounded-2xl border border-blue-400/20 bg-blue-400/[0.04] p-8" data-id="element-1748" data-mp="4yocnsb2epg9">
              
              <div className="flex items-center gap-3 mb-4" data-id="element-1749" data-mp="vs1gmnutvmc">
                <div className="w-10 h-10 rounded-xl bg-blue-400/20 flex items-center justify-center" data-id="element-1750" data-mp="3s34fwtxcruyk">
                  <BuildingIcon className="w-5 h-5 text-blue-400" data-id="element-1751" data-mp="2lmsiiv2cuh41" />
                </div>
                <div data-id="element-1752" data-mp="2gnz1wtrru9zn">
                  <h3 className="text-lg font-semibold text-white" data-id="element-1753" data-mp="2gnfva97kn80x">
                    Thruline Enterprise
                  </h3>
                  <p className="text-blue-400 text-xs font-medium" data-id="element-1754" data-mp="1dzmpppaokmp5">
                    You're here
                  </p>
                </div>
              </div>
              <p className="text-white/55 text-sm leading-relaxed mb-4" data-id="element-1755" data-mp="1np6cv75ff2pt">
                A fully custom ERP built and maintained by Thruline. We handle
                the system. You handle the business.
              </p>
              <ul className="space-y-2" data-id="element-1756" data-mp="1xlrk7z4x4pwt">
                {['Thruline builds the entire system', 'Thruline operates and maintains', 'Changes handled by Thruline\'s team', 'Best for: companies that want it done'].map((item, i) => <li key={i} className="flex items-start gap-2 text-white/45 text-sm" data-id="element-1757" data-mp="26mz3b9yv5k6b" data-mpi={i}>
                  
                    <CheckIcon className="w-3.5 h-3.5 text-blue-400 mt-0.5 flex-shrink-0" data-id="element-1758" data-mp="21oxoj18h070n" />
                    {item}
                  </li>)}
              </ul>
            </motion.div>

            {/* Core Card */}
            <motion.div variants={fadeUp} className="rounded-2xl border border-white/[0.06] bg-white/[0.03] p-8" data-id="element-1759" data-mp="7chju4vbp0xw">
              
              <div className="flex items-center gap-3 mb-4" data-id="element-1760" data-mp="2nwjsbn0f1gi3">
                <div className="w-10 h-10 rounded-xl bg-violet-electric/15 flex items-center justify-center" data-id="element-1761" data-mp="2nw1red2t6e3u">
                  <ZapIcon className="w-5 h-5 text-violet-electric" data-id="element-1762" data-mp="2a3lt1pkur499" />
                </div>
                <h3 className="text-lg font-semibold text-white" data-id="element-1763" data-mp="2e4xah7tkoj6d">
                  Thruline Core
                </h3>
              </div>
              <p className="text-white/55 text-sm leading-relaxed mb-4" data-id="element-1764" data-mp="256v1a4zoqk80">
                A custom-developed ERP you own and evolve. Thruline helps you
                plan and build it. You understand it, modify it, and grow it.
              </p>
              <ul className="space-y-2 mb-6" data-id="element-1765" data-mp="1v8nw0jerdqlu">
                {['Thruline builds the first version with you', 'You operate with Thruline advisory', 'You make changes with AI assistance', 'Best for: companies that want to own it'].map((item, i) => <li key={i} className="flex items-start gap-2 text-white/45 text-sm" data-id="element-1766" data-mp="12dpu24aycc5n" data-mpi={i}>
                  
                    <CheckIcon className="w-3.5 h-3.5 text-violet-electric mt-0.5 flex-shrink-0" data-id="element-1767" data-mp="xewfe8jxif3w" />
                    {item}
                  </li>)}
              </ul>
              <a href="#/solutions/custom" className="inline-flex items-center gap-2 text-violet-electric text-sm font-medium hover:underline" data-id="element-1768" data-mp="tdb4gsqygawc">
                
                Learn about Core
                <ArrowRightIcon className="w-3.5 h-3.5" data-id="element-1769" data-mp="1l3fmikr8slue" />
              </a>
            </motion.div>
          </motion.div>

          <motion.p initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="text-white/35 text-sm text-center leading-relaxed max-w-2xl mx-auto" data-id="element-1770" data-mp="1h4zv84o4orb6">
            
            Some customers start with Enterprise and transition to Core as
            their team builds confidence. Others start with Core and bring in
            Enterprise for major expansions. The two products complement each
            other.
          </motion.p>
        </div>
      </section>

      {/* ── SECTION 9: BOTTOM CTA ── */}
      <section className="relative w-full py-24 px-6 overflow-hidden" data-id="element-1771" data-mp="2ha0ry55jcqpc">
        <div className="absolute inset-0" style={{
        background: 'linear-gradient(135deg, #0D0B2E 0%, #1A1452 30%, #3b82f6 70%, #818cf8 100%)'
      }} data-id="element-1772" data-mp="2lcnr6r4jddwk" />
        
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
        backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E\")"
      }} data-id="element-1773" data-mp="2valftfqb5cpp" />
        

        <motion.div className="relative z-10 max-w-3xl mx-auto text-center" initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.6
      }} data-id="element-1774" data-mp="3e0b55nbax6a8">
          
          <span className="text-white/60 text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1775" data-mp="3a346prdvwkry">
            Get Started
          </span>
          <h2 className="text-3xl sm:text-5xl font-bold text-white tracking-tight mb-6" data-id="element-1776" data-mp="357f2haqx6fpb">
            Your business already knows
            <br data-id="element-1777" data-mp="2deekxpz4z80p" />
            how it works. Let us read it.
          </h2>
          <p className="text-white/60 text-lg mb-10 max-w-xl mx-auto" data-id="element-1778" data-mp="2rdo2asz2w869">
            Discovery is free. We'll read your documents, map your processes,
            and show you what your custom ERP looks like — before you commit to
            anything.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-1779" data-mp="6lvp98n2jmxr">
            <a href="https://calendly.com/alex-thruline/learn-about-thruline" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-navy font-semibold text-sm hover:bg-white/90 transition-all hover:shadow-lg hover:shadow-white/10" data-id="element-1780" data-mp="1nkfexwrxcfj">
              
              Start with Discovery
              <ArrowRightIcon className="w-4 h-4" data-id="element-1781" data-mp="1amvcsm7kqcf3" />
            </a>
            <a href="https://calendly.com/alex-thruline/learn-about-thruline" className="inline-flex items-center px-8 py-3.5 rounded-full border border-white/30 text-white font-medium text-sm hover:border-white/60 transition-all" data-id="element-1782" data-mp="1fjlsh8fgv5zz">
              
              Talk to our team
            </a>
          </div>
        </motion.div>
      </section>
    </div>;
}